// redux/slices/appointmentsSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl } from "../../Utils/serverSelector";
import { clearJwtToken } from "../../Utils/authStorage";
import { apiRequest } from "../../Utils/apiClient";

/* ========================= HELPERS ========================= */

const getError = (error, fallback) =>
  error?.response?.data ||
  error?.message ||
  fallback;

const syncFlatAppointments = (state) => {
  state.appointments = Object.values(state.byUid || {}).flat();
};

/* ========================= PRIVATE THUNKS ========================= */

export const addAppointment = createAsyncThunk(
  "appointments/addAppointment",
  async ({ uid, appointment }, { rejectWithValue }) => {
    try {
      const response = await apiRequest("POST", "/new-appointment", {
        ...appointment,
        uid,
      });

      return response?.data || response;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }

      return rejectWithValue(getError(error, "addAppointment failed"));
    }
  }
);

export const fetchAppointments = createAsyncThunk(
  "appointments/fetchAppointments",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const response = await apiRequest("GET", `/new-appointment/${uid}`);
      return response?.data || response;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }

      if (error?.message === "timeout of 0ms exceeded") return null;

      return rejectWithValue(getError(error, "fetchAppointments failed"));
    }
  }
);

export const fetchAppointmentsByRange = createAsyncThunk(
  "appointments/fetchByRange",
  async ({ uid, startDate, endDate }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const response = await apiRequest(
        "GET",
        `/new-appointment/${uid}?startDate=${startDate}&endDate=${endDate}`
      );

      return {
        uid,
        startDate,
        endDate,
        appointments: response?.data || response,
      };
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }

      return rejectWithValue(getError(error, "fetchAppointmentsByRange failed"));
    }
  }
);

export const fetchAppointmentsByPhone = createAsyncThunk(
  "appointments/fetchAppointmentsByPhone",
  async (phone, { rejectWithValue }) => {
    try {
      if (!phone) throw new Error("Телефон не передан");

      const response = await apiRequest(
        "GET",
        `/new-appointment/by-phone/${encodeURIComponent(phone)}`
      );

      return response?.data || response;
    } catch (error) {
      return rejectWithValue(
        getError(error, "fetchAppointmentsByPhone failed")
      );
    }
  }
);

export const updateAppointment = createAsyncThunk(
  "appointments/updateAppointment",
  async ({ id, appointment }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID встречи не передан");

      const response = await apiRequest(
        "PUT",
        `/new-appointment/appointment/${id}`,
        appointment
      );

      return response?.data || response;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }

      return rejectWithValue(getError(error, "updateAppointment failed"));
    }
  }
);

export const deleteAppointment = createAsyncThunk(
  "appointments/deleteAppointment",
  async (id, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID встречи не передан");

      const response = await apiRequest(
        "DELETE",
        `/new-appointment/appointment/${id}`
      );

      if (response?.status && response.status !== 200) {
        throw new Error(`Ошибка удаления. Код: ${response.status}`);
      }

      return id;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }

      return rejectWithValue(getError(error, "deleteAppointment failed"));
    }
  }
);

export const fetchAllAppointments = createAsyncThunk(
  "appointments/fetchAllAppointments",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest("GET", "/new-appointment");
      return response?.data || response;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }

      return rejectWithValue(getError(error, "fetchAllAppointments failed"));
    }
  }
);

export const fetchAppointmentById = createAsyncThunk(
  "appointments/fetchAppointmentById",
  async (sendId, { rejectWithValue }) => {
    try {
      if (!sendId) throw new Error("sendId не передан");

      const response = await apiRequest(
        "GET",
        `/new-appointment/appointment/by-sendId/${sendId}`
      );

      return response?.data || response;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }

      return rejectWithValue(getError(error, "fetchAppointmentById failed"));
    }
  }
);

export const updateAppointmentAndRefetch = createAsyncThunk(
  "appointments/updateAndRefetch",
  async ({ id, appointment, uid }, { dispatch }) => {
    await dispatch(updateAppointment({ id, appointment })).unwrap();
    if (uid) await dispatch(fetchAppointments(uid));
    return { id };
  }
);

export const batchUpdateAppointments = createAsyncThunk(
  "appointments/batchUpdate",
  async (updates, { dispatch }) => {
    const results = await Promise.allSettled(
      updates.map((item) => dispatch(updateAppointment(item)).unwrap())
    );

    return results;
  }
);

/* ========================= PUBLIC THUNKS ========================= */

export const fetchPublicAppointments = createAsyncThunk(
  "appointments/fetchPublicAppointments",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const response = await axios.get(
        `${getServerUrl()}/public/${uid}/appointments/`
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "fetchPublicAppointments failed"
      );
    }
  }
);

export const addPublicAppointment = createAsyncThunk(
  "appointments/addPublicAppointment",
  async ({ uid, appointment }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const base = getServerUrl().replace(/\/$/, "");

      const payload = {
        sendID: appointment?.sendID || Math.random().toString(36).slice(2, 10),
        ...appointment,
        uid,
      };

      const response = await axios.post(
        `${base}/public/${uid}/appointments/`,
        payload
      );

      return response.data?.appointment || response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "addPublicAppointment failed"
      );
    }
  }
);

export const fetchPublicAppointmentById = createAsyncThunk(
  "appointments/fetchPublicAppointmentById",
  async (id, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID встречи не передан");

      const response = await axios.get(
        `${getServerUrl()}/public/by-sendId/${id}`
      );

      return response.data?.appointment || response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "fetchPublicAppointmentById failed"
      );
    }
  }
);

export const updatePublicAppointment = createAsyncThunk(
  "appointments/updatePublicAppointment",
  async ({ id, appointment }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID встречи не передан");

      const base = getServerUrl().replace(/\/$/, "");

      const response = await axios.put(
        `${base}/public/appointment/${id}`,
        appointment
      );

      return response.data?.appointment || response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "updatePublicAppointment failed"
      );
    }
  }
);

export const fetchPublicAppointmentPage = createAsyncThunk(
  "appointments/fetchPublicAppointmentPage",
  async (sendId, { rejectWithValue }) => {
    try {
      if (!sendId) throw new Error("sendId не передан");

      const base = getServerUrl().replace(/\/$/, "");
      const response = await axios.get(
        `${base}/public/appointment-page/${sendId}`
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "fetchPublicAppointmentPage failed"
      );
    }
  }
);

/* ========================= SELECTORS ========================= */

export const selectAppointmentsForUid = (state, uid) =>
  state.appointments.byUid?.[String(uid)] || [];

export const selectAppointmentsForSalon = createSelector(
  [(state) => state.appointments.byUid, (state, uids) => uids],
  (byUid, uids = []) => {
    if (!byUid || !uids.length) return [];

    return uids.flatMap((uid) => byUid[String(uid)] || []);
  }
);

export const selectAppointmentsByDate = createSelector(
  [(state) => state.appointments.byUid, (state, date) => date],
  (byUid, date) => {
    if (!byUid || !date) return [];

    return Object.keys(byUid).flatMap((uid) =>
      (byUid[uid] || []).filter(
        (appointment) =>
          (appointment.date || appointment.dateOfAppointment) === date
      )
    );
  }
);

/* ========================= STATE ========================= */

const initialState = {
  appointments: [],
  byUid: {},
  allAppointments: [],
  selectedAppointment: null,
  status: "idle",
  error: null,
};

/* ========================= SLICE ========================= */

const appointmentsSlice = createSlice({
  name: "appointments",
  initialState,
  reducers: {
    patchAppointmentLocal: (state, action) => {
      const { id, changes } = action.payload || {};
      if (!id) return;

      const patch = changes || {};

      const index = state.appointments.findIndex(
        (item) => String(item?.id) === String(id)
      );

      if (index !== -1) {
        state.appointments[index] = {
          ...state.appointments[index],
          ...patch,
        };
      }

      Object.keys(state.byUid || {}).forEach((uid) => {
        const arr = state.byUid[uid] || [];
        const itemIndex = arr.findIndex(
          (item) => String(item?.id) === String(id)
        );

        if (itemIndex !== -1) {
          arr[itemIndex] = {
            ...arr[itemIndex],
            ...patch,
          };
        }

        state.byUid[uid] = arr;
      });
    },

    removeAppointmentLocal: (state, action) => {
      const id = action.payload;

      state.appointments = state.appointments.filter(
        (item) => String(item?.id) !== String(id)
      );

      Object.keys(state.byUid || {}).forEach((uid) => {
        state.byUid[uid] = (state.byUid[uid] || []).filter(
          (item) => String(item?.id) !== String(id)
        );
      });
    },

    clearSelectedAppointment: (state) => {
      state.selectedAppointment = null;
    },

    clearAppointmentsError: (state) => {
      state.error = null;
    },

    resetAppointmentsStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },

    clearAppointments: (state) => {
      state.appointments = [];
      state.byUid = {};
      state.allAppointments = [];
      state.selectedAppointment = null;
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addAppointment.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(addAppointment.fulfilled, (state, action) => {
        state.status = "succeeded";

        const appointment = action.payload?.appointment || action.payload;
        if (!appointment) return;

        state.appointments.push(appointment);

        const uid = appointment.uid || appointment._fetchedForUid;
        if (uid != null) {
          const key = String(uid);
          if (!state.byUid[key]) state.byUid[key] = [];
          state.byUid[key].push(appointment);
          syncFlatAppointments(state);
        }
      })
      .addCase(addAppointment.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "addAppointment failed";
      })

      .addCase(fetchAppointments.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAppointments.fulfilled, (state, action) => {
        state.status = "succeeded";

        const uid = String(action.meta.arg);
        const appointments = Array.isArray(action.payload)
          ? action.payload.map((item) => ({
              ...item,
              _fetchedForUid: uid,
            }))
          : [];

        state.byUid[uid] = appointments;
        syncFlatAppointments(state);
      })
      .addCase(fetchAppointments.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "fetchAppointments failed";
      })

      .addCase(fetchAppointmentsByRange.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAppointmentsByRange.fulfilled, (state, action) => {
        state.status = "succeeded";

        const { uid, appointments } = action.payload || {};
        if (!uid || !Array.isArray(appointments)) return;

        const key = String(uid);
        const incoming = appointments.map((item) => ({
          ...item,
          _fetchedForUid: key,
        }));

        const existing = state.byUid[key] || [];
        const map = new Map(existing.map((item) => [String(item.id), item]));

        incoming.forEach((item) => {
          map.set(String(item.id), item);
        });

        state.byUid[key] = Array.from(map.values());
        syncFlatAppointments(state);
      })
      .addCase(fetchAppointmentsByRange.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchAppointmentsByRange failed";
      })

      .addCase(updateAppointment.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateAppointment.fulfilled, (state, action) => {
        state.status = "succeeded";

        const updated = action.payload?.appointment || action.payload;
        if (!updated?.id) return;

        const id = String(updated.id);

        const index = state.appointments.findIndex(
          (item) => String(item?.id) === id
        );

        if (index !== -1) {
          state.appointments[index] = updated;
        }

        Object.keys(state.byUid || {}).forEach((uid) => {
          const arr = state.byUid[uid] || [];
          const itemIndex = arr.findIndex(
            (item) => String(item?.id) === id
          );

          if (itemIndex !== -1) {
            arr[itemIndex] = updated;
          }

          state.byUid[uid] = arr;
        });

        if (String(state.selectedAppointment?.id) === id) {
          state.selectedAppointment = updated;
        }
      })
      .addCase(updateAppointment.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "updateAppointment failed";
      })

      .addCase(deleteAppointment.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteAppointment.fulfilled, (state, action) => {
        state.status = "succeeded";

        const id = String(action.payload);

        state.appointments = state.appointments.filter(
          (item) => String(item?.id) !== id
        );

        Object.keys(state.byUid || {}).forEach((uid) => {
          state.byUid[uid] = (state.byUid[uid] || []).filter(
            (item) => String(item?.id) !== id
          );
        });

        if (String(state.selectedAppointment?.id) === id) {
          state.selectedAppointment = null;
        }
      })
      .addCase(deleteAppointment.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "deleteAppointment failed";
      })

      .addCase(fetchAllAppointments.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAllAppointments.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.allAppointments = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchAllAppointments.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchAllAppointments failed";
      })

      .addCase(fetchAppointmentById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAppointmentById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selectedAppointment = action.payload || null;
      })
      .addCase(fetchAppointmentById.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchAppointmentById failed";
      })

      .addCase(fetchAppointmentsByPhone.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAppointmentsByPhone.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.allAppointments = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchAppointmentsByPhone.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchAppointmentsByPhone failed";
      })

      .addCase(fetchPublicAppointments.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPublicAppointments.fulfilled, (state, action) => {
        state.status = "succeeded";

        const uid = String(action.meta.arg);
        const appointments = Array.isArray(action.payload)
          ? action.payload.map((item) => ({
              ...item,
              _fetchedForUid: uid,
            }))
          : [];

        state.byUid[uid] = appointments;
        syncFlatAppointments(state);
      })
      .addCase(fetchPublicAppointments.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchPublicAppointments failed";
      })

      .addCase(addPublicAppointment.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(addPublicAppointment.fulfilled, (state, action) => {
        state.status = "succeeded";

        const appointment = action.payload;
        if (!appointment) return;

        state.appointments.push(appointment);

        const uid = appointment.uid || appointment._fetchedForUid;
        if (uid != null) {
          const key = String(uid);
          if (!state.byUid[key]) state.byUid[key] = [];
          state.byUid[key].push(appointment);
          syncFlatAppointments(state);
        }
      })
      .addCase(addPublicAppointment.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "addPublicAppointment failed";
      })

      .addCase(fetchPublicAppointmentById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPublicAppointmentById.fulfilled, (state, action) => {
        state.status = "succeeded";

        const appointment = action.payload || null;
        state.selectedAppointment = appointment;

        if (!appointment?.id) return;

        const id = String(appointment.id);

        const flatIndex = state.appointments.findIndex(
          (item) => String(item?.id) === id
        );

        if (flatIndex === -1) {
          state.appointments.push(appointment);
        } else {
          state.appointments[flatIndex] = appointment;
        }

        const uid = appointment.uid || appointment._fetchedForUid;
        if (uid != null) {
          const key = String(uid);
          if (!state.byUid[key]) state.byUid[key] = [];

          const index = state.byUid[key].findIndex(
            (item) => String(item?.id) === id
          );

          if (index === -1) {
            state.byUid[key].push(appointment);
          } else {
            state.byUid[key][index] = appointment;
          }

          syncFlatAppointments(state);
        }
      })
      .addCase(fetchPublicAppointmentById.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchPublicAppointmentById failed";
      })

      .addCase(updatePublicAppointment.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updatePublicAppointment.fulfilled, (state, action) => {
        state.status = "succeeded";

        const updated = action.payload?.appointment || action.payload;
        if (!updated?.id) return;

        const id = String(updated.id);

        const index = state.appointments.findIndex(
          (item) => String(item?.id) === id
        );

        if (index !== -1) {
          state.appointments[index] = updated;
        }

        Object.keys(state.byUid || {}).forEach((uid) => {
          const arr = state.byUid[uid] || [];
          const itemIndex = arr.findIndex(
            (item) => String(item?.id) === id
          );

          if (itemIndex !== -1) {
            arr[itemIndex] = updated;
          }

          state.byUid[uid] = arr;
        });

        if (String(state.selectedAppointment?.id) === id) {
          state.selectedAppointment = updated;
        }
      })
      .addCase(updatePublicAppointment.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "updatePublicAppointment failed";
      })

      .addCase(fetchPublicAppointmentPage.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPublicAppointmentPage.fulfilled, (state, action) => {
        state.status = "succeeded";

        const { appointment } = action.payload || {};
        if (!appointment?.id) return;

        state.selectedAppointment = appointment;

        const uid = appointment.uid;
        if (uid != null) {
          const key = String(uid);
          if (!state.byUid[key]) state.byUid[key] = [];

          const index = state.byUid[key].findIndex(
            (item) => String(item?.id) === String(appointment.id)
          );

          if (index === -1) {
            state.byUid[key].push(appointment);
          } else {
            state.byUid[key][index] = appointment;
          }

          syncFlatAppointments(state);
        }
      })
      .addCase(fetchPublicAppointmentPage.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchPublicAppointmentPage failed";
      })

      .addCase(resetStore, () => initialState);
  },
});

/* ========================= ACTIONS ========================= */

export const {
  patchAppointmentLocal,
  removeAppointmentLocal,
  clearSelectedAppointment,
  clearAppointmentsError,
  resetAppointmentsStatus,
  clearAppointments,
} = appointmentsSlice.actions;

export default appointmentsSlice.reducer;